import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface Props {
  enabled?: boolean;
  minIntervalMs?: number;
  maxIntervalMs?: number;
  visibleMs?: number;
  firstDelayMs?: number;
  onAppear?: () => void;
}

const NAMES = [
  'Alex Martínez',
  'Ana García',
  'Carlos Rodríguez',
  'María López',
  'Diego Hernández',
  'Sofía Pérez',
  'Luis Castillo',
  'Camila Romero',
  'Andrés Vargas',
  'Valentina Torres',
  'Mateo Ramírez',
  'Isabella Cruz',
  'Sebastián Morales',
  'Lucía Jiménez',
  'Daniel Ortega',
  'Gabriela Mendoza',
  'Javier Reyes',
  'Natalia Aguilar',
  'Esteban Vega',
  'Daniela Ruiz',
  'Pablo Sánchez',
  'Antonella Castro',
  'Tomás Flores',
  'Renata Domínguez',
  'Joaquín Silva',
  'Mariana Núñez',
  'Felipe Cordero',
  'Catalina Paredes',
  'Nicolás Bermúdez',
  'Emilia Salgado',
  'Adrián Quintero',
  'Paula Restrepo',
  'Sergio Cabrera',
  'Andrea Acosta',
  'Manuel Rojas',
  'Carolina Espinoza',
  'Iván Soto',
  'Fernanda Galindo',
  'Cristóbal Méndez',
  'Valeria Cárdenas',
];

function pickName(exclude: string | null): string {
  let name = NAMES[Math.floor(Math.random() * NAMES.length)];
  if (exclude && name === exclude) {
    name = NAMES[(NAMES.indexOf(name) + 1) % NAMES.length];
  }
  return name;
}

export function SocialProofTicker({
  enabled = true,
  minIntervalMs = 15000,
  maxIntervalMs = 20000,
  visibleMs = 4500,
  firstDelayMs = 4000,
  onAppear,
}: Props) {
  const [current, setCurrent] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setCurrent(null);
      return;
    }

    let nextTimer: ReturnType<typeof setTimeout> | null = null;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;

    const scheduleNext = (delay: number) => {
      nextTimer = setTimeout(() => {
        setCurrent(prev => pickName(prev));
        onAppear?.();
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(() => setCurrent(null), visibleMs);

        const jitter = minIntervalMs + Math.random() * (maxIntervalMs - minIntervalMs);
        scheduleNext(jitter);
      }, delay);
    };

    scheduleNext(firstDelayMs);

    return () => {
      if (nextTimer) clearTimeout(nextTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [enabled, minIntervalMs, maxIntervalMs, visibleMs, firstDelayMs, onAppear]);

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-xs z-50 pointer-events-none"
      aria-live="polite"
      aria-atomic="true"
    >
      <AnimatePresence>
        {current && (
          <motion.div
            key={current + Date.now()}
            initial={{ opacity: 0, x: -40, y: 0 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="bg-hit-white text-hit-black rounded-lg shadow-2xl px-4 py-3 flex items-center gap-3 border-l-4 border-hit-gold"
          >
            <span className="text-2xl" aria-hidden="true">✅</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight truncate">{current}</p>
              <p className="text-xs text-hit-gray-dark leading-tight">
                Agendó su sesión estratégica · hace un momento
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
